// Global footer behavior: mobile disclosure columns + Back to Top button.

((Drupal, once) => {
  const DESKTOP_QUERY = '(min-width: 900px)';
  const SCROLL_THRESHOLD = 400;
  let disclosureId = 0;

  /**
   * Gives a temporarily focusable element focus without leaving it tabbable.
   */
  const focusTemporarily = (element) => {
    if (!element) {
      return;
    }
    const hadTabindex = element.hasAttribute('tabindex');
    if (!hadTabindex) {
      element.setAttribute('tabindex', '-1');
      element.addEventListener(
        'blur',
        () => element.removeAttribute('tabindex'),
        { once: true },
      );
    }
    element.focus({ preventScroll: true });
  };

  /**
   * Initializes one footer instance.
   */
  const initializeFooter = (footer) => {
    const cleanup = [];
    const desktop = window.matchMedia(DESKTOP_QUERY);
    const disclosures = Array.from(
      footer.querySelectorAll('.global-footer__column'),
    )
      .map((column) => {
        const heading = column.querySelector('.global-footer__column-title');
        const label = column.querySelector('.global-footer__column-label');
        const button = column.querySelector('.global-footer__column-button');
        const panel = column.querySelector('.global-footer__column-panel');
        if (!heading || !label || !button || !panel) {
          return null;
        }

        disclosureId += 1;
        button.id = `global-footer-heading-${disclosureId}`;
        button.setAttribute(
          'aria-controls',
          `global-footer-panel-${disclosureId}`,
        );
        panel.id = `global-footer-panel-${disclosureId}`;
        panel.setAttribute('aria-labelledby', button.id);

        return {
          button,
          heading,
          label,
          panel,
        };
      })
      .filter(Boolean);

    const setExpanded = (disclosure, open) => {
      const { button, panel } = disclosure;
      button.setAttribute('aria-expanded', String(open));
      if (open) {
        panel.removeAttribute('hidden');
        panel.removeAttribute('inert');
      } else {
        if (panel.contains(document.activeElement)) {
          button.focus();
        }
        panel.setAttribute('hidden', '');
        panel.setAttribute('inert', '');
      }
    };

    disclosures.forEach((disclosure) => {
      const onClick = () => {
        const open =
          disclosure.button.getAttribute('aria-expanded') === 'true';
        setExpanded(disclosure, !open);
      };
      disclosure.button.addEventListener('click', onClick);
      cleanup.push(() =>
        disclosure.button.removeEventListener('click', onClick),
      );
    });

    const applyDisclosureMode = () => {
      disclosures.forEach((disclosure) => {
        const { button, heading, label, panel } = disclosure;
        if (desktop.matches) {
          if (document.activeElement === button) {
            focusTemporarily(heading);
          }
          label.removeAttribute('hidden');
          button.setAttribute('hidden', '');
          button.removeAttribute('aria-expanded');
          panel.removeAttribute('hidden');
          panel.removeAttribute('inert');
        } else {
          label.setAttribute('hidden', '');
          button.removeAttribute('hidden');
          setExpanded(disclosure, false);
        }
      });
    };

    applyDisclosureMode();
    if (typeof desktop.addEventListener === 'function') {
      desktop.addEventListener('change', applyDisclosureMode);
      cleanup.push(() =>
        desktop.removeEventListener('change', applyDisclosureMode),
      );
    } else if (typeof desktop.addListener === 'function') {
      desktop.addListener(applyDisclosureMode);
      cleanup.push(() => desktop.removeListener(applyDisclosureMode));
    }

    const backToTop = footer.querySelector('.global-footer__back-to-top');
    if (backToTop) {
      const reduceMotion = window.matchMedia(
        '(prefers-reduced-motion: reduce)',
      );
      const toggleBackToTop = () => {
        backToTop.classList.toggle(
          'is-visible',
          window.scrollY > SCROLL_THRESHOLD,
        );
      };
      let ticking = false;
      const onScroll = () => {
        if (!ticking) {
          window.requestAnimationFrame(() => {
            toggleBackToTop();
            ticking = false;
          });
          ticking = true;
        }
      };
      const onBackToTop = () => {
        const main =
          document.getElementById('main') || document.querySelector('main');
        focusTemporarily(main);
        window.scrollTo({
          top: 0,
          behavior: reduceMotion.matches ? 'auto' : 'smooth',
        });
      };

      backToTop.addEventListener('click', onBackToTop);
      window.addEventListener('scroll', onScroll, { passive: true });
      cleanup.push(() => {
        backToTop.removeEventListener('click', onBackToTop);
        window.removeEventListener('scroll', onScroll);
      });
      toggleBackToTop();
    }

    footer.amacoeFooterCleanup = () => {
      cleanup.forEach((callback) => callback());
      delete footer.amacoeFooterCleanup;
    };
  };

  if (Drupal && once) {
    Drupal.behaviors.amacoeGlobalFooter = {
      attach(context) {
        once('amacoe-global-footer', '.global-footer', context).forEach(
          initializeFooter,
        );
      },

      detach(context, settings, trigger) {
        if (trigger !== 'unload') {
          return;
        }
        once
          .remove('amacoe-global-footer', '.global-footer', context)
          .forEach((footer) => footer.amacoeFooterCleanup?.());
      },
    };
  } else {
    const attachStandalone = () => {
      document.querySelectorAll('.global-footer').forEach((footer) => {
        if (!footer.dataset.amacoeGlobalFooter) {
          footer.dataset.amacoeGlobalFooter = 'true';
          initializeFooter(footer);
        }
      });
    };
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', attachStandalone);
    } else {
      attachStandalone();
    }
  }
})(window.Drupal, window.once);
